import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import SubPageInnerContentHolder from '../../../components/subPageContentHolder/SubPageInnerContentHolder';
import { HOME_ROUTE } from '../../../lib/constants/route';
import LoadingSpinner from '../../../ui/loadingSpinner/LoadingSpinner';
import get_action_proposals_api_call from '../../../lib/api/actions/get-action-proposals';
import { ActionProposal } from '../../../lib/api/actions/action-types';
import ActionProposalCard from '../actionQueue/ActionProposalCard';
import '../actions.scss';

const ActionHistory = () => {
  const { t } = useTranslation();

  const [proposals, setProposals] = useState<ActionProposal[]>([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [platformFilter, setPlatformFilter] = useState<string>('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  const statusTabs = [
    { label: t('All'), value: '' },
    { label: t('Completed'), value: 'completed' },
    { label: t('Failed'), value: 'failed' },
    { label: t('Denied'), value: 'denied' },
    { label: t('Expired'), value: 'expired' },
    { label: t('RolledBack'), value: 'rolled_back' },
  ];

  const fetchProposals = useCallback(async () => {
    try {
      // For history, if no specific filter, fetch all completed-type statuses
      const statusParam = statusFilter || 'completed,failed,denied,expired,rolled_back';

      const result = await get_action_proposals_api_call({
        status: statusParam,
        platform: platformFilter || undefined,
        page: currentPage,
        per_page: 20,
      });

      if (result) {
        setProposals(result.data);
        setTotalPages(result.last_page);
      } else {
        setProposals([]);
        setTotalPages(1);
      }
    } catch (error) {
      console.error('Failed to load action history:', error);
    } finally {
      setLoading(false);
    }
  }, [statusFilter, platformFilter, currentPage]);

  useEffect(() => {
    setLoading(true);
    setCurrentPage(1);
  }, [statusFilter, platformFilter]);

  useEffect(() => {
    fetchProposals();
  }, [fetchProposals]);

  const renderPagination = () => {
    if (totalPages <= 1) return null;

    const pages: number[] = [];
    for (let i = 1; i <= totalPages; i++) {
      pages.push(i);
    }

    return (
      <div className='action-pagination'>
        <button
          className='action-pagination__btn'
          disabled={currentPage === 1}
          onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
        >
          {t('Previous')}
        </button>
        {pages.map((page) => (
          <button
            key={page}
            className={`action-pagination__btn ${
              page === currentPage ? 'action-pagination__btn--active' : ''
            }`}
            onClick={() => setCurrentPage(page)}
          >
            {page}
          </button>
        ))}
        <button
          className='action-pagination__btn'
          disabled={currentPage === totalPages}
          onClick={() =>
            setCurrentPage((prev) => Math.min(totalPages, prev + 1))
          }
        >
          {t('Next')}
        </button>
      </div>
    );
  };

  return (
    <SubPageInnerContentHolder
      title={t('ActionHistory')}
      backBtnLink={HOME_ROUTE}
    >
      {/* Filters */}
      <div className='action-queue-filters'>
        <div className='action-queue-filters__status-group'>
          {statusTabs.map((tab) => (
            <button
              key={tab.value}
              className={`action-queue-filters__filter-btn ${
                statusFilter === tab.value
                  ? 'action-queue-filters__filter-btn--active'
                  : ''
              }`}
              onClick={() => setStatusFilter(tab.value)}
            >
              {tab.label}
            </button>
          ))}
        </div>

        <select
          className='action-queue-filters__platform-select'
          value={platformFilter}
          onChange={(e) => setPlatformFilter(e.target.value)}
        >
          <option value=''>{t('AllPlatforms')}</option>
          <option value='facebook_ads'>Facebook Ads</option>
          <option value='google_ads'>Google Ads</option>
          <option value='tiktok_ads'>TikTok Ads</option>
          <option value='linkedin_ads'>LinkedIn Ads</option>
        </select>
      </div>

      {/* Content */}
      {loading ? (
        <div style={{ marginTop: '2rem', textAlign: 'center' }}>
          <LoadingSpinner customLoadingMsg={t('LoadingActionHistory')} />
        </div>
      ) : proposals.length === 0 ? (
        <div className='action-empty-state'>
          <p className='action-empty-state__text'>
            {t('NoActionHistoryFound')}
          </p>
        </div>
      ) : (
        <>
          {proposals.map((proposal) => (
            <ActionProposalCard
              key={proposal.id}
              proposal={proposal}
              showActions={false}
            />
          ))}
          {renderPagination()}
        </>
      )}
    </SubPageInnerContentHolder>
  );
};

export default ActionHistory;
